import { OFFLINE_MESSAGE, TIMEOUT_MESSAGE, unexpectedStatus } from "./messages";
import type {
  Highlight,
  HighlightResponse,
  ListResponse,
  MailInput,
  PastShowsResponse,
  Picture,
  Settings,
  Show,
  Track,
  Video,
} from "./types";

/**
 * Where the CMS lives.
 *
 * Before this port the backend was guessed from window.location.hostname,
 * because a site served as static files has no build step that could inject a
 * value. There is a build step now, so the environment decides: VITE_ variables
 * are baked into the bundle by vite build, and vite.config.ts refuses to build
 * without this one. The fallback below can therefore only ever apply to
 * `npm run dev` on a developer machine.
 */
export const API_BASE_URL = (
  import.meta.env.VITE_API_BASE_URL?.trim() || "http://localhost:3000"
).replace(/\/+$/, "");

/**
 * How long to wait for an answer.
 *
 * Generous on purpose. The CMS runs on a dyno that sleeps after 30 minutes
 * without traffic, and a cold start costs several seconds - the first visitor
 * of the day hits it regularly. Cutting that off early would turn a slow page
 * into a broken one. The limit exists so a request that will never be answered
 * ends in a message with a retry button instead of a spinner that turns for
 * ever, which is what the old site did.
 */
export const REQUEST_TIMEOUT_MS = 45_000;

/** A request that did not produce usable data, with German wording ready. */
export class ApiError extends Error {
  constructor(
    /** The HTTP status, or 0 when there was no answer at all. */
    readonly status: number,
    /** What to show the visitor, already in German. */
    readonly messages: string[],
  ) {
    super(messages[0] ?? unexpectedStatus(status));
    this.name = "ApiError";
  }

  /** The server rejected what was sent. */
  get isValidation(): boolean {
    return this.status === 400;
  }

  /** Too many requests from this address. */
  get isRateLimited(): boolean {
    return this.status === 429;
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
}

async function request<T>(
  path: string,
  { method = "GET", body }: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  // An explicit controller rather than AbortSignal.timeout(): the timer is
  // cleared as soon as the whole answer is in, so nothing stays pending.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller.signal,
    });

    if (!response.ok) {
      // The status is the whole answer. The body of an error is not worth
      // reading: for the four public endpoints the CMS collapses every
      // rejection to one of three fixed English strings, and a 502 from the
      // platform in front of it is an HTML page rather than JSON at all.
      throw new ApiError(response.status, [unexpectedStatus(response.status)]);
    }

    // A successful answer that is not JSON is not data. Returning null here -
    // which is what a tolerant catch would do - hands the caller something to
    // destructure, and the TypeError that follows is English and reaches the
    // visitor through the error box. It has to be an ApiError like any other
    // failure, so it is worded in German and offers the retry button.
    let payload: unknown;
    try {
      payload = await response.json();
    } catch {
      throw new ApiError(response.status, [unexpectedStatus(response.status)]);
    }

    return payload as T;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    // No answer at all, so there is no status to report. The two cases read
    // differently to a visitor: one is their connection, the other is a
    // backend that is taking too long.
    const timedOut = error instanceof Error && error.name === "AbortError";
    throw new ApiError(0, [timedOut ? TIMEOUT_MESSAGE : OFFLINE_MESSAGE]);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Unwraps a list endpoint, or says so when the answer is not one.
 *
 * The CMS is a separate deployment. A renamed or dropped `items` would
 * otherwise show up as an empty gallery and an empty show list - "there is
 * nothing" rather than "this did not work" - which is the one error state a
 * visitor cannot tell apart from the truth.
 */
function itemsOf<T>(payload: ListResponse<T>, status = 200): T[] {
  if (!payload || !Array.isArray(payload.items)) {
    throw new ApiError(status, [unexpectedStatus(status)]);
  }
  return payload.items;
}

/** The shapes the start page can render. Anything else is nothing to show. */
const KNOWN_HIGHLIGHT_KINDS: string[] = ["picture", "video", "track"];

/**
 * Unwraps the highlight endpoint, and decides what "nothing to show" is.
 *
 * Four answers are the contract: a picture, a video, a recording, or null. Two
 * more are possible in practice and are handled differently on purpose.
 *
 * A missing `highlight` key is drift, the same failure `itemsOf` guards
 * against: a renamed field would empty the start page's highlight area and
 * look exactly like an editor having published none. That is reported.
 *
 * A `kind` this version does not know is not drift but a newer CMS - a kind
 * added after this deployment, as `track` itself was. There is nothing to
 * render and nothing a visitor could do about it, so the area simply stays
 * away, like null. An error box in its place would announce a failure to
 * somebody who is not missing anything, and a section with a heading and no
 * content would be worse still.
 */
function highlightOf(
  payload: HighlightResponse,
  status = 200,
): Highlight | null {
  if (!payload || typeof payload !== "object" || !("highlight" in payload)) {
    throw new ApiError(status, [unexpectedStatus(status)]);
  }

  const highlight = payload.highlight;
  if (!highlight) {
    return null;
  }

  // A string list rather than a union comparison: the value comes off the wire
  // and can be anything, which is precisely what this line is here to find out.
  return KNOWN_HIGHLIGHT_KINDS.includes(highlight.kind) ? highlight : null;
}

/**
 * The public API of the CMS, one function per endpoint.
 *
 * The four endpoints the old site used - getEventInfos, getAllEvents,
 * getPicturesURL - are deliberately absent. /api/sendMail is unchanged; it
 * never had a v2.
 */
export const api = {
  /** Ascending, the next show first. Sorted by the server. */
  async upcomingShows(): Promise<Show[]> {
    return itemsOf(await request<ListResponse<Show>>("/api/v2/shows/upcoming"));
  },

  /**
   * Descending, the most recently played first, limited to the window an
   * editor set in the admin. `years` says which window was applied.
   */
  async pastShows(): Promise<PastShowsResponse> {
    const payload = await request<PastShowsResponse>("/api/v2/shows/past");
    itemsOf(payload);
    return payload;
  },

  /** The gallery, in the order an editor dragged it into. */
  async pictures(): Promise<Picture[]> {
    return itemsOf(await request<ListResponse<Picture>>("/api/v2/pictures"));
  },

  /**
   * The videos of the media page, in the order an editor arranged them.
   *
   * Only the visible and available ones are in the answer - the server decides
   * that, as it does for the gallery. Nothing here sorts or filters again.
   */
  async videos(): Promise<Video[]> {
    return itemsOf(await request<ListResponse<Video>>("/api/v2/videos"));
  },

  /**
   * The recordings of the music section on Medien, in the order an editor
   * arranged them.
   *
   * Only the visible and available ones are in the answer - the server decides
   * that, as it does for the videos and the gallery. Nothing here sorts or
   * filters again, and nothing checks `type` against a list of known ones: the
   * embed address is built from it, and an address for a type this version has
   * never heard of is more likely to work than to be wrong.
   */
  async tracks(): Promise<Track[]> {
    return itemsOf(await request<ListResponse<Track>>("/api/v2/tracks"));
  },

  /**
   * The values every page needs rather than one: today the accounts the footer
   * links to.
   *
   * Fetched once per visit - see composables/useSettings.ts - so this is the
   * one endpoint whose answer outlives the page that asked for it.
   *
   * `socialLinks` is guarded the way `items` is: the CMS is a separate
   * deployment, and a renamed field would otherwise reach the footer as
   * undefined and take the page down with it. A failure here costs a visitor
   * nothing, because the footer treats "no answer" and "no accounts" alike.
   */
  async settings(): Promise<Settings> {
    const payload = await request<Settings>("/api/v2/settings");
    if (!payload || !Array.isArray(payload.socialLinks)) {
      throw new ApiError(200, [unexpectedStatus(200)]);
    }
    return payload;
  },

  /**
   * What the start page shows under the show carousel: a picture, a video, a
   * recording, or nothing. Null is an ordinary answer, not a failure - the
   * area is then not on the page at all.
   */
  async highlight(): Promise<Highlight | null> {
    return highlightOf(await request<HighlightResponse>("/api/v2/highlight"));
  },

  /** The contact form. Answers 400 on invalid input and 429 when rate limited. */
  sendMail(input: MailInput): Promise<{ message: string }> {
    return request<{ message: string }>("/api/sendMail", {
      method: "POST",
      body: input,
    });
  },
};
